<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import Spinner from 'shared/components/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const agents = ref([]);
const availableExtensions = ref([]);
// agentId -> extension
const assignments = ref({});
const isLoading = ref(false);
const isCreating = ref(false);

const load = async () => {
  isLoading.value = true;
  try {
    const membersResponse = await store.dispatch('inboxMembers/get', {
      inboxId: props.inbox.id,
    });
    agents.value = membersResponse?.data?.payload || [];

    const { data } = await InboxesAPI.getZadarmaExtensions(props.inbox.id);
    availableExtensions.value = data.available || [];
    assignments.value = Object.fromEntries(
      (data.assignments || []).map(a => [a.agent_id, a.extension])
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.ZADARMA_EXTENSIONS.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

const createExtension = async () => {
  isCreating.value = true;
  try {
    const { data } = await InboxesAPI.createZadarmaExtension(props.inbox.id);
    const created = (data.numbers || []).join(', ');
    await load();
    useAlert(t('INBOX_MGMT.ZADARMA_EXTENSIONS.CREATED', { number: created }));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.ZADARMA_EXTENSIONS.CREATE_ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};

const onChange = async (agent, extension) => {
  try {
    if (extension) {
      await InboxesAPI.setZadarmaExtension(props.inbox.id, agent.id, extension);
      assignments.value = { ...assignments.value, [agent.id]: extension };
    } else {
      await InboxesAPI.deleteZadarmaExtension(props.inbox.id, agent.id);
      const next = { ...assignments.value };
      delete next[agent.id];
      assignments.value = next;
    }
    useAlert(t('INBOX_MGMT.ZADARMA_EXTENSIONS.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.ZADARMA_EXTENSIONS.SAVE_ERROR')
    );
    load();
  }
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <div class="flex items-start justify-between gap-4 mb-4">
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.ZADARMA_EXTENSIONS.DESCRIPTION') }}
      </p>
      <NextButton
        sm
        faded
        icon="i-lucide-plus"
        :label="t('INBOX_MGMT.ZADARMA_EXTENSIONS.CREATE')"
        :is-loading="isCreating"
        class="shrink-0"
        @click="createExtension"
      />
    </div>

    <div v-if="isLoading" class="flex justify-center py-8">
      <Spinner />
    </div>

    <div v-else-if="!agents.length" class="py-6 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.ZADARMA_EXTENSIONS.NO_AGENTS') }}
    </div>

    <div v-else class="flex flex-col gap-2">
      <div
        v-for="agent in agents"
        :key="agent.id"
        class="flex items-center justify-between gap-4 p-3 border rounded-lg border-n-weak"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ agent.name }}
          </span>
          <span class="text-xs truncate text-n-slate-10">{{
            agent.email
          }}</span>
        </div>
        <select
          :value="assignments[agent.id] || ''"
          class="w-40 h-8 px-2 text-sm bg-white border rounded-md border-n-weak text-n-slate-12 dark:bg-n-solid-2"
          @change="onChange(agent, $event.target.value)"
        >
          <option value="">
            {{ t('INBOX_MGMT.ZADARMA_EXTENSIONS.UNASSIGNED') }}
          </option>
          <option v-for="ext in availableExtensions" :key="ext" :value="ext">
            {{ ext }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>
